/**
 * T036: New Onboarding Wizard for User Story 2
 * 
 * Simplified onboarding wizard that matches T035 backend API structure
 * Constitutional compliance: <2s page loads, WCAG 2.1 AA accessibility, TDD coverage
 */

import React, { useState, useCallback, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  LinearProgress,
  Step,
  StepLabel,
  Stepper,
  Typography,
  Alert,
  Grid
} from '@mui/material';

import useOnboardingService, { OnboardingData, OnboardingSession } from '../../services/onboardingService';
import { CompanyInfoStep } from './steps/CompanyInfoStep';
import { ProjectDetailsStep } from './steps/ProjectDetailsStep';
import { TechRequirementsStep } from './steps/TechRequirementsStep';
import { ReviewStep } from './steps/ReviewStep';

interface OnboardingWizardV2Props {
  onComplete: (projectId: number) => void;
  onError: (error: string) => void;
}

const OnboardingWizardV2: React.FC<OnboardingWizardV2Props> = ({
  onComplete,
  onError
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sessionId, setSessionId] = useState<number | null>(null);
  const [initialized, setInitialized] = useState(false);
  
  // Data structure matching T035 backend API
  const [onboardingData, setOnboardingData] = useState<OnboardingData>({
    company_info: {
      company_name: '',
      company_website: '',
      company_size: '',
      industry: '',
      primary_contact_name: '',
      primary_contact_email: '',
      primary_contact_phone: '',
      company_description: ''
    },
    project_details: {
      project_name: '',
      project_description: '',
      project_types: [],
      priority_level: '',
      budget_range: '',
      timeline: '',
      expected_start_date: '',
      expected_end_date: ''
    },
    tech_requirements: {
      target_platforms: [],
      preferred_technologies: [],
      integration_requirements: [],
      performance_requirements: '',
      security_requirements: '',
      scalability_requirements: '',
      accessibility_requirements: ''
    },
    review: {
      terms_accepted: false,
      privacy_policy_accepted: false,
      communication_preferences: [],
      additional_notes: ''
    }
  });

  const {
    startOnboarding,
    getCurrentOnboardingSession,
    updateOnboardingStep,
    navigateToStep,
    completeOnboarding,
    validateStepData,
    OnboardingServiceError
  } = useOnboardingService();

  const steps = ['Company Information', 'Project Details', 'Technical Requirements', 'Review & Submit'];
  const stepKeys: (keyof OnboardingData)[] = ['company_info', 'project_details', 'tech_requirements', 'review'];

  // Initialize onboarding session
  useEffect(() => {
    const initializeSession = async () => {
      if (initialized) return;

      try {
        setLoading(true);
        setError(null);

        // Check for existing session first
        const existingSession = await getCurrentOnboardingSession();
        if (existingSession) {
          setSessionId(existingSession.id);
          setActiveStep(stepKeys.indexOf(existingSession.current_step as keyof OnboardingData));
          if (existingSession.step_data) {
            setOnboardingData(prev => ({
              ...prev,
              ...existingSession.step_data
            }));
          }
        } else {
          // Start new session
          const newSession = await startOnboarding();
          setSessionId(newSession.id);
          setActiveStep(0);
        }

        setInitialized(true);
      } catch (err) {
        const errorMessage = err instanceof OnboardingServiceError 
          ? err.message 
          : 'Failed to initialize onboarding session';
        setError(errorMessage);
        onError(errorMessage);
        // CRITICAL: Set initialized to true even on error to prevent infinite loops
        setInitialized(true);
      } finally {
        setLoading(false);
      }
    };

    initializeSession();
  }, []); // Empty dependency array - only run once on mount

  // Handle step data changes
  const handleStepDataChange = useCallback((
    step: keyof OnboardingData, 
    data: Partial<OnboardingData[keyof OnboardingData]>
  ) => {
    setOnboardingData(prev => ({
      ...prev,
      [step]: { ...prev[step], ...data }
    }));
  }, []);

  // Validate current step
  const validateCurrentStep = useCallback((): boolean => {
    const currentStepKey = stepKeys[activeStep];
    const currentStepData = onboardingData[currentStepKey];
    
    try {
      const validation = validateStepData(currentStepKey, currentStepData);
      if (!validation.isValid) {
        const errorMessages = Object.values(validation.errors);
        setError(errorMessages.join(', '));
        return false;
      }
      return true;
    } catch (err) {
      setError('Validation failed');
      return false;
    }
  }, [activeStep, onboardingData, validateStepData, stepKeys]);

  // Navigate to next step
  const handleNext = useCallback(async () => {
    if (!validateCurrentStep()) return;
    
    try {
      setLoading(true);
      setError(null);

      const currentStepKey = stepKeys[activeStep];
      
      // Update backend with current step data
      if (sessionId) {
        await updateOnboardingStep(currentStepKey, onboardingData[currentStepKey]);
      }

      if (activeStep < steps.length - 1) {
        setActiveStep(prev => prev + 1);
      }
    } catch (err) {
      const errorMessage = err instanceof OnboardingServiceError 
        ? err.message 
        : 'Failed to save progress';
      setError(errorMessage);
      onError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [activeStep, steps.length, validateCurrentStep, sessionId, updateOnboardingStep, 
      onboardingData, stepKeys, OnboardingServiceError, onError]);

  // Navigate to previous step
  const handleBack = useCallback(() => {
    if (activeStep > 0) {
      setActiveStep(prev => prev - 1);
    }
  }, [activeStep]);

  // Complete onboarding
  const handleComplete = useCallback(async () => {
    if (!validateCurrentStep() || !sessionId) return;

    try {
      setLoading(true);
      setError(null);

      const currentStepKey = stepKeys[activeStep];
      
      // Ensure the final step (review) data is submitted before completing
      if (sessionId) {
        await updateOnboardingStep(currentStepKey, onboardingData[currentStepKey]);
      }

      // Complete the onboarding process
      const result = await completeOnboarding(true); // Create project
      
      if (result.project) {
        onComplete(result.project.id);
      } else {
        throw new Error('No project created');
      }
    } catch (err) {
      const errorMessage = err instanceof OnboardingServiceError 
        ? err.message 
        : 'Failed to complete onboarding';
      setError(errorMessage);
      onError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [validateCurrentStep, sessionId, completeOnboarding, onComplete, onError, OnboardingServiceError, updateOnboardingStep, activeStep, onboardingData, stepKeys]);

  // Render current step content
  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <CompanyInfoStep
            data={onboardingData.company_info}
            onChange={(data: Partial<OnboardingData['company_info']>) => handleStepDataChange('company_info', data)}
            errors={{}}
          />
        );
      case 1:
        return (
          <ProjectDetailsStep
            data={onboardingData.project_details}
            onChange={(data: Partial<OnboardingData['project_details']>) => handleStepDataChange('project_details', data)}
            errors={{}}
          />
        );
      case 2:
        return (
          <TechRequirementsStep
            data={onboardingData.tech_requirements}
            onChange={(data: Partial<OnboardingData['tech_requirements']>) => handleStepDataChange('tech_requirements', data)}
            errors={{}}
          />
        );
      case 3:
        return (
          <ReviewStep
            data={onboardingData.review}
            allData={onboardingData}
            onChange={(data: Partial<OnboardingData['review']>) => handleStepDataChange('review', data)}
            errors={{}}
          />
        );
      default:
        return null;
    }
  };

  if (!initialized) {
    return (
      <Container maxWidth="md">
        <Box sx={{ py: 4, textAlign: 'center' }}>
          <Typography variant="h6" gutterBottom>
            Initializing onboarding session...
          </Typography>
          <LinearProgress sx={{ mt: 2 }} />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md">
      <Box sx={{ py: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Client Onboarding
        </Typography>
        
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>

            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            {loading && <LinearProgress sx={{ mb: 3 }} />}

            <Box sx={{ minHeight: 400 }}>
              {renderStepContent(activeStep)}
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'row', pt: 2 }}>
              <Button
                color="inherit"
                disabled={activeStep === 0 || loading}
                onClick={handleBack}
                sx={{ mr: 1 }}
              >
                Back
              </Button>
              <Box sx={{ flex: '1 1 auto' }} />
              
              {activeStep === steps.length - 1 ? (
                <Button
                  variant="contained"
                  onClick={handleComplete}
                  disabled={loading || !onboardingData.review.terms_accepted || !onboardingData.review.privacy_policy_accepted}
                >
                  Complete Onboarding
                </Button>
              ) : (
                <Button
                  variant="contained"
                  onClick={handleNext}
                  disabled={loading}
                >
                  Next
                </Button>
              )}
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
};

export default OnboardingWizardV2;